import React, { useState, useEffect, FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ItemSearch from './components/ItemSearch';
import ItemDetailsDisplay from './components/ItemDetailsDisplay';
import UpdateCertificatePage from './components/UpdateCertificatePage';
import Header from './pages/Header';
import CertificateIssuancePage from './pages/CertificateIssuancePage';
import UserProfileDropdown from './components/UserProfileDropdown';
import AuthButton from './components/AuthButton';
import IntroSection from './components/IntroSection';
import CertificateHistoryPage from './pages/CertificateHistoryPage';
import AlertModal from './components/AlertModal';
import IPFSLoadingModal from './components/IPFSLoadingModal';
import StatusUpdatePage from './pages/StatusUpdatePage';
import { getItemDetails } from './services/web3Service';
import LanguageSwitcher from './components/LanguageSwitcher';
import './App.css';

interface CertificateData {
    name: string;
    description: string;
    image: string | null;
    attributes?: { trait_type: string; value: string | number }[];
}
interface StatusHistoryEntry {
    statusName: string;
    timestamp: number;
    notes: string;
    latitude?: string;
    longitude?: string;
}
interface ItemData {
    id: string;
    certificate: CertificateData;
    statusHistory: StatusHistoryEntry[];
    initialCertificateURI: string;
    currentCertificateURI: string;
    historicalCertificateURIs: string[];
}

const AppContent: FC = () => {
    const { t } = useTranslation();
    const { user, isAuthenticated, smartAccountStatus, dismissSmartAccountStatus } = useAuth();

    const handleGoHome = () => {
        setCurrentView('search');
        setItemData(null);
        setLastSearchedId(null);
    };

    const [currentView, setCurrentView] = useState<'search' | 'mint' | 'updateStatus' | 'updateCertificate' | 'certificateHistory'>('search');
    const [alertOpen, setAlertOpen] = useState(false);
    const [alertTitle, setAlertTitle] = useState('');
    const [alertMessage, setAlertMessage] = useState('');
    const [alertType, setAlertType] = useState<'success' | 'error'>('success');

    const [itemData, setItemData] = useState<ItemData | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [lastSearchedId, setLastSearchedId] = useState<string | null>(null);
    const [templateData, setTemplateData] = useState<{
        name: string;
        description: string;
        categories: string[];
        rawMaterials: { value: string; label: string }[];
        vintageYear: string;
        imageUrl?: string;
        latitude?: number;
        longitude?: number;
    } | null>(null);

    // For now, authenticated users can mint/update - in production this would come from the backend roles
    const [isCertificateContractOwner, setIsCertificateContractOwner] = useState<boolean>(false);
    const [isTrackerContractOwner, setIsTrackerContractOwner] = useState<boolean>(false);

    // Set owner status based on authenticated user
    useEffect(() => {
        if (isAuthenticated && user) {
            // In production, check user role from backend
            setIsCertificateContractOwner(true);
            setIsTrackerContractOwner(true);
        } else {
            setIsCertificateContractOwner(false);
            setIsTrackerContractOwner(false);
        }
    }, [isAuthenticated, user]);

    const showAlert = (title: string, message: string, type: 'success' | 'error') => {
        setAlertTitle(title);
        setAlertMessage(message);
        setAlertType(type);
        setAlertOpen(true);
    };

    const handleSearch = useCallback(async (itemId: string) => {
        if (!itemId) {
            setItemData(null);
            setLastSearchedId(null);
            return;
        }
        setLoading(true);
        setItemData(null);
        setLastSearchedId(itemId);

        try {
            const data = await getItemDetails(itemId);
            setItemData(data);
        } catch (e: any) {
            console.error("Search failed:", e);
            if (e.message && e.message.includes("ERC721NonexistentToken")) {
                showAlert(t('searchErrorTitle'), t('tokenNotFound'), 'error');
            } else {
                showAlert(t('searchErrorTitle'), e.message || t('fetchError'), 'error');
            }
        } finally {
            setLoading(false);
        }
    }, [t]);

    // Check for certificate token parameter in URL and auto-search
    // Note: Skip if this looks like an OAuth callback (has 'refresh' or 'user' params)
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const tokenParam = urlParams.get('token');
        const isOAuthCallback = urlParams.has('refresh') || urlParams.has('user');

        // Only search if it's a certificate token ID (numeric), not an OAuth JWT
        if (tokenParam && !isOAuthCallback && /^\d+$/.test(tokenParam)) {
            handleSearch(tokenParam);
            window.history.replaceState({}, document.title, window.location.pathname);
        }
    }, [handleSearch]);

    const handleCertificateMinted = (tokenId: string) => {
        setCurrentView('search');
        setTemplateData(null);
        handleSearch(tokenId);
    };

    const handleStatusUpdated = (tokenId: string) => {
        setCurrentView('search');
        handleSearch(tokenId);
    };

    const handleCertificateUpdated = (tokenId: string) => {
        setCurrentView('search');
        handleSearch(tokenId);
    };

    const handleUseAsTemplate = () => {
        if (!itemData) return;
        const cert = itemData.certificate;
        const attrs = cert.attributes || [];
        setTemplateData({
            name: cert.name,
            description: cert.description,
            categories: attrs.filter(a => a.trait_type === "Category").map(a => String(a.value)),
            rawMaterials: attrs
                .filter(a => a.trait_type === "Raw Material" || a.trait_type === "Raw Materials")
                .map(a => ({ value: String(a.value).toLowerCase().replace(/\W/g, ''), label: String(a.value) })),
            vintageYear: attrs.find(a => a.trait_type === "Vintage Year")?.value?.toString() || '',
            imageUrl: cert.image || undefined,
            ...(() => {
                const loc = attrs.find(a => a.trait_type === "Location");
                if (loc && typeof loc.value === 'string') {
                    const parts = loc.value.split(',').map(p => parseFloat(p.trim()));
                    if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
                        return { latitude: parts[0], longitude: parts[1] };
                    }
                }
                return {};
            })(),
        });
        setCurrentView('mint');
    };

    return (
        <div className="app-container">
            <header className="app-header">
                <div className="header-left">
                    <Header onNavigateHome={handleGoHome} />
                    <h1>{t('appTitle')}</h1>
                </div>
                <div className="header-controls">
                    <div className="language-switcher-wrapper">
                        <LanguageSwitcher />
                    </div>
                    {isAuthenticated ? (
                        <UserProfileDropdown
                            onNavigateToMint={() => setCurrentView('mint')}
                        />
                    ) : (
                        <AuthButton />
                    )}
                </div>
            </header>

            {smartAccountStatus.isCreating && (
                <div className="smart-account-banner info">
                    <span className="banner-spinner" />
                    {t('smartAccount.creating')}
                </div>
            )}
            {smartAccountStatus.address && (
                <div className="smart-account-banner success">
                    <span>{t('smartAccount.created')}</span>
                    <a
                        href={`https://sepolia.etherscan.io/address/${smartAccountStatus.address}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="banner-link"
                    >
                        {t('smartAccount.viewOnEtherscan')}
                    </a>
                    <button className="banner-dismiss" onClick={dismissSmartAccountStatus}>&times;</button>
                </div>
            )}
            {smartAccountStatus.error && (
                <div className="smart-account-banner error">
                    <span>{t('smartAccount.error')}: {smartAccountStatus.error}</span>
                    <button className="banner-dismiss" onClick={dismissSmartAccountStatus}>&times;</button>
                </div>
            )}

            {currentView === 'search' && (
                <>
                    <ItemSearch onSearch={handleSearch} disabled={loading} />
                    {!loading && itemData && (
                        <ItemDetailsDisplay
                            item={itemData}
                            userAddress={user?.id || null}
                            isCertificateContractOwner={isCertificateContractOwner}
                            isTrackerContractOwner={isTrackerContractOwner}
                            onNavigateToUpdateStatus={() => setCurrentView('updateStatus')}
                            onNavigateToUpdateCertificate={() => setCurrentView('updateCertificate')}
                            onNavigateToCertificateHistory={() => setCurrentView('certificateHistory')}
                            onUseAsTemplate={handleUseAsTemplate}
                        />
                    )}
                    {!loading && !itemData && (
                        <IntroSection />
                    )}
                </>
            )}

            {currentView === 'mint' && isAuthenticated && (
                <CertificateIssuancePage onCertificateMinted={handleCertificateMinted} onBack={() => { setCurrentView('search'); setTemplateData(null); }} templateData={templateData} />
            )}

            {currentView === 'updateStatus' && isAuthenticated && isTrackerContractOwner && itemData && (
                <StatusUpdatePage onStatusUpdated={handleStatusUpdated} onBack={() => setCurrentView('search')} item={itemData} />
            )}

            {currentView === 'updateCertificate' && isAuthenticated && isCertificateContractOwner && itemData && (
                <UpdateCertificatePage
                    tokenId={itemData.id}
                    onCertificateUpdated={handleCertificateUpdated}
                    onBack={() => setCurrentView('search')}
                />
            )}

            {currentView === 'certificateHistory' && itemData && (
                <CertificateHistoryPage
                    tokenId={itemData.id}
                    initialUri={itemData.initialCertificateURI}
                    historicalUris={itemData.historicalCertificateURIs}
                    onBack={() => setCurrentView('search')}
                />
            )}

            <IPFSLoadingModal
                isOpen={loading && currentView === 'search'}
                tokenId={lastSearchedId}
                transactionHash={null}
            />

            <AlertModal
                isOpen={alertOpen}
                onClose={() => setAlertOpen(false)}
                title={alertTitle}
                message={alertMessage}
                type={alertType}
            />

            <footer className="app-footer">
                <p>{t('footer', { year: new Date().getFullYear() })}</p>
            </footer>
        </div>
    );
};

const App: FC = () => {
    return (
        <AuthProvider>
            <AppContent />
        </AuthProvider>
    );
};

export default App;
